import { create } from "zustand";
import { Level } from "@prisma-remote/client";
import dico from "src/shared/util/src/language/dico";
import { LevelRemoteWithAll } from "src/shared/util/src";
import { apiClient } from "src/services/apiClient";

export interface CreateLevelData {
  minTotal: number;
  maxTotal: number;
  name: string;
  ageCategoryId: number;
  weightCategoryId: number;
  disciplineId: number;
  sheetDate: string;
  /** Required for Admin; Licensed can omit (server uses JWT organisation). */
  organisationId?: string;
}

export interface UpdateLevelData {
  minTotal?: number;
  maxTotal?: number;
  name?: string;
  ageCategoryId?: number;
  weightCategoryId?: number;
  disciplineId?: number;
  sheetDate?: string;
  organisationId?: string;
}

interface LevelState {
  levels: LevelRemoteWithAll[];
  selectedLevel: Level | null;
  loading: boolean;
  error: string | null;
  // Actions
  setLevels: (levels: LevelRemoteWithAll[]) => void;
  setSelectedLevel: (level: Level | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  // API calls
  fetchLevels: (
    includeAll?: boolean,
    organisationId?: string,
    effective?: boolean,
  ) => Promise<void>;
  fetchLevel: (id: number, token: string) => Promise<void>;
  createLevel: (data: CreateLevelData, token: string) => Promise<void>;
  updateLevel: (
    id: number,
    data: UpdateLevelData,
    token: string,
  ) => Promise<void>;
  deleteLevel: (id: number, token: string) => Promise<void>;
  importLevels: (
    file: File,
    disciplineCode: "FA" | "PL" | "DC" | "BP",
    organisationId: string,
    token: string,
  ) => Promise<void>;
  /**
   * Loads levels for the account organisation.
   * When `effective` is true, resolves `levelSourceId` (single source, never cumulative).
   */
  fetchMyLevels: (includeAll?: boolean, effective?: boolean) => Promise<void>;
  /** Downloads the portable workbook for the account's organisation. */
  downloadMyPortableWorkbook: (includeLevels: boolean) => Promise<Blob>;
  /** Replaces the account organisation's levels from a portable workbook. */
  importMyPortableWorkbook: (file: File) => Promise<{ imported: number }>;
}

export const useLevelStore = create<LevelState>((set) => ({
  levels: [],
  selectedLevel: null,
  loading: false,
  error: null,
  // Setters
  setLevels: (levels) => set({ levels }),
  setSelectedLevel: (selectedLevel) => set({ selectedLevel }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  // Fetch all levels (GET /levels is public - used on anonymous speaker/stream/group pages)
  fetchLevels: async (includeAll = false, organisationId, effective) => {
    set({ loading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (includeAll) {
        params.append("includeAll", "true");
      }
      if (organisationId) params.append("organisationId", organisationId);
      if (effective) params.append("effective", "true");
      const query = params.toString();
      // Backend has no JWT on list; skip client auth so anonymous viewers get minima
      const levels = await apiClient.get<LevelRemoteWithAll[]>(
        `/levels${query ? `?${query}` : ""}`,
        { requiresAuth: false },
      );
      set({ levels, loading: false });
    } catch {
      set({
        error: dico().admin.niveaux.errors.importError,
        loading: false,
      });
    }
  },
  // Fetch one level by ID
  fetchLevel: async (id, token) => {
    set({ loading: true, error: null });
    try {
      void token;
      const level = await apiClient.get<Level>(`/levels/${id}`);
      set({ selectedLevel: level, loading: false });
    } catch {
      set({
        error: dico().admin.niveaux.errors.importError,
        loading: false,
      });
    }
  },
  // Create a new level
  createLevel: async (data, token) => {
    set({ loading: true, error: null });
    try {
      void token;
      const newLevel = await apiClient.post<LevelRemoteWithAll>(
        "/levels",
        data,
      );
      set((state) => ({
        levels: [newLevel, ...state.levels],
        loading: false,
      }));
    } catch (error) {
      set({
        error:
          (error as { message?: string })?.message ||
          dico().admin.niveaux.errors.importError,
        loading: false,
      });
      throw error;
    }
  },
  // Update a level
  updateLevel: async (id, data, token) => {
    set({ loading: true, error: null });
    try {
      void token;
      const updatedLevel = await apiClient.patch<Level>(`/levels/${id}`, data);
      set((state) => ({
        levels: state.levels.map((level) =>
          level.id === id
            ? (updatedLevel as unknown as LevelRemoteWithAll)
            : level,
        ),
        selectedLevel:
          state.selectedLevel?.id === id ? updatedLevel : state.selectedLevel,
        loading: false,
      }));
    } catch (error) {
      set({
        error: dico().admin.niveaux.errors.importError,
        loading: false,
      });
      throw error;
    }
  },
  // Delete a level
  deleteLevel: async (id, token) => {
    set({ loading: true, error: null });
    try {
      void token;
      await apiClient.delete<void>(`/levels/${id}`);
      set((state) => ({
        levels: state.levels.filter((level) => level.id !== id),
        selectedLevel:
          state.selectedLevel?.id === id ? null : state.selectedLevel,
        loading: false,
      }));
    } catch (error) {
      set({
        error: dico().admin.niveaux.errors.importError,
        loading: false,
      });
      throw error;
    }
  },
  // Import levels from Excel file
  importLevels: async (file, disciplineCode, organisationId, token) => {
    set({ loading: true, error: null });
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("disciplineCode", disciplineCode);
      formData.append("organisationId", organisationId);

      void token;
      await apiClient.post<void>("/levels/import", formData);
      {
        // Refresh levels after import (includeAll=true for admin pages)
        const levels = await apiClient.get<LevelRemoteWithAll[]>(
          `/levels?includeAll=true&organisationId=${encodeURIComponent(organisationId)}`,
        );
        set({ levels, loading: false });
      }
    } catch (error) {
      set({
        error:
          (error as { message?: string })?.message ||
          dico().admin.niveaux.errors.importError,
        loading: false,
      });
      throw error;
    }
  },
  fetchMyLevels: async (includeAll = true, effective = true) => {
    set({ loading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (includeAll) params.set("includeAll", "true");
      if (effective) params.set("effective", "true");
      const query = params.toString();
      const levels = await apiClient.get<LevelRemoteWithAll[]>(
        `/levels/me${query ? `?${query}` : ""}`,
      );
      set({ levels, loading: false });
    } catch {
      set({
        error: dico().admin.niveaux.errors.importError,
        loading: false,
      });
    }
  },
  downloadMyPortableWorkbook: async (includeLevels) => {
    const response = await apiClient.authorizedFetch(
      `/levels/portable-template-me?includeLevels=${includeLevels}`,
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.blob();
  },
  importMyPortableWorkbook: async (file) => {
    set({ loading: true, error: null });
    try {
      const formData = new FormData();
      formData.append("file", file);
      const result = await apiClient.post<{ imported: number }>(
        "/levels/import-portable-me",
        formData,
      );
      // Reload effective levels (single source via levelSourceId).
      const levels = await apiClient.get<LevelRemoteWithAll[]>(
        "/levels/me?includeAll=true&effective=true",
      );
      set({ levels, loading: false });
      return result;
    } catch (error) {
      set({
        error:
          (error as { message?: string })?.message ||
          dico().admin.niveaux.errors.importError,
        loading: false,
      });
      throw error;
    }
  },
}));
